import React, { useState, useEffect, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Input,
  Label,
  Text,
  Portal,
} from '@fluentui/react-components';
import {
  ChevronDownRegular,
  PersonRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    position: 'relative',
    width: '100%',
  },
  input: {
    width: '100%',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow8,
    maxHeight: '200px',
    overflowY: 'auto',
    zIndex: 1000,
    marginTop: '4px',
  },
  option: {
    padding: '8px 12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    ':last-child': {
      borderBottom: 'none',
    },
  },
  selectedOption: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
  },
  noResults: {
    padding: '12px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  optionName: {
    fontWeight: tokens.fontWeightMedium,
  },
  optionDetails: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  inputContainer: {
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: tokens.colorNeutralForeground3,
    pointerEvents: 'none',
  },
});

interface Option {
  id: string;
  name: string;
  email?: string;
  role?: string;
  businessUnit?: string;
}

interface SearchableDropdownProps {
  label: string;
  placeholder: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helpText?: string;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  required = false,
  disabled = false,
  error,
  helpText,
}) => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term
  useEffect(() => {
    const filtered = options.filter(option =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.businessUnit?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
    setSelectedIndex(-1);
  }, [searchTerm, options]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && filteredOptions[selectedIndex]) {
          handleOptionSelect(filteredOptions[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle option selection
  const handleOptionSelect = (option: Option) => {
    onChange(option.name);
    setSearchTerm('');
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      setSearchTerm('');
    }
  };

  // Handle input blur
  const handleInputBlur = () => {
    // Delay closing to allow for option clicks
    setTimeout(() => {
      setIsOpen(false);
      setSearchTerm('');
      setSelectedIndex(-1);
    }, 200);
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    
    // If user clears the input, clear the selection
    if (!newValue) {
      onChange('');
    }
  };

  // Get display value
  const getDisplayValue = () => {
    if (searchTerm) return searchTerm;
    if (value) return value;
    return '';
  };



  return (
    <div className={styles.container} ref={containerRef}>
      <Label htmlFor="searchable-dropdown" required={required}>
        {label}
      </Label>
      <div className={styles.inputContainer}>
        <Input
          ref={inputRef}
          id="searchable-dropdown"
          value={getDisplayValue()}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          appearance={error ? 'outline' : 'outline'}
          contentAfter={
            <div className={styles.inputIcon}>
              <ChevronDownRegular />
            </div>
          }
          className={styles.input}
        />
      </div>
      {error && (
        <Text size={100} style={{ color: tokens.colorPaletteRedForeground2 }}>
          {error}
        </Text>
      )}
      {helpText && !error && (
        <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
          {helpText}
        </Text>
      )}

      {isOpen && (
        <Portal>
          <div className={styles.dropdown}>
            {filteredOptions.length === 0 ? (
              <div className={styles.noResults}>
                No results found
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={option.id}
                  className={`${styles.option} ${index === selectedIndex ? styles.selectedOption : ''}`}
                  onClick={() => handleOptionSelect(option)}
                >
                  <PersonRegular />
                  <div>
                    <div className={styles.optionName}>{option.name}</div>
                    <div className={styles.optionDetails}>
                      {option.role && `${option.role}`}
                      {option.businessUnit && option.role && ' • '}
                      {option.businessUnit && `${option.businessUnit}`}
                      {option.email && (option.role || option.businessUnit) && ' • '}
                      {option.email && option.email}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Portal>
      )}
    </div>
  );
};

export default SearchableDropdown; 